/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React from 'react';
import {
    object, arrayOf, shape, string, bool, func,
} from 'prop-types';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

// Material UI
import { withStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';

// Helpers
import { dateMMMMDDYYYY } from '../../utils/dateString';
import { deepObjectExtend } from '../../utils/object';
import { combineStyle } from '../../style/combineStyle';
import { getAddToCartError } from '../../../../../state/ducks/AddToCart/AddToCart-Selectors';
import { main as MainStyle } from '../../style/main';
import { DesktopOrderListStyle } from '../../style/DesktopOrderListStyle';
import { getCryptedOrderDetailsURL } from '../../utils/crypto';

// Components
import DesktopOrderListItem from './DesktopOrderListItem';
import { getAppRoutePrefix } from '../../../../helpers/common/helper';

const styles = ({ typography, palette }) => deepObjectExtend(
    combineStyle(typography, palette, {
        MainStyle,
        DesktopOrderListStyle,
    }),
);

const DesktopOrderList = ({
    classes, ROSSOrder, userProfile, showTrackingWidgetForSameLogicalItems,
}) => {
    const history = useHistory();
    const addToCartError = useSelector((state) => getAddToCartError(state));
    const ROSSOrderDateString = dateMMMMDDYYYY(ROSSOrder?.orderPlacedDate);

    const scrollTop = () => {
        if (typeof window !== 'undefined') {
            window.scroll({
                top: 0,
                left: 0,
                behavior: 'smooth',
            });
        }
    };

    const handleViewDetailsClicked = async (
        uuid,
        orderNumber,
        isLegacyVCOrder,
    ) => {
        if (orderNumber && uuid) {
            // redirect to the order detail page
            const ordersURL = getCryptedOrderDetailsURL(orderNumber, uuid, null, null, isLegacyVCOrder);
            history.push(`${getAppRoutePrefix()}${ordersURL}`);
        }
    };

    return (
        <div className={classes.DesktopOrderListStyle}>
            <Grid container>
                <Grid item xs={12}>
                    <Grid key={ROSSOrder.intOrderNumber} className={`${classes.orderProductMain} order-list-wrapper`}>
                        <Grid className="orderHeader" container>
                            {/* View / Manage Order */}
                            <Grid container item xs={4}>
                                <Button
                                    variant="contained"
                                    className="quaternary text-transform-none"
                                    onClick={() => handleViewDetailsClicked(
                                        ROSSOrder.uuid,
                                        ROSSOrder.orderNumber,
                                        ROSSOrder.vtcorder,
                                    )}
                                >
                                    {ROSSOrder.modifyOrder === 'true'
                                        ? 'View / Manage Order'
                                        : 'View Order'}
                                </Button>
                            </Grid>
                            {/* Order # | Replacement For: */}
                            <Grid container item xs={4}>
                                {/* Order # */}
                                <Grid className="orderHeaderItem">
                                    <span className="headerItemLabel">Order #: </span>
                                    <span>{ROSSOrder.orderNumber}</span>
                                </Grid>
                                {/* Replacement For: */}
                                {ROSSOrder.parentOrderNumber
                                    ? (
                                        <Grid className="orderHeaderItem">
                                            <span className="headerItemLabel">Replacement For: </span>
                                            <span>{ROSSOrder.parentOrderNumber}</span>
                                        </Grid>
                                    )
                                    : <Grid className="orderHeaderItem" />}
                            </Grid>
                            {/* Order Date: */}
                            <Grid className="orderHeaderItem" item xs={4}>
                                <span className="headerItemLabel">Order Date: </span>
                                <span className="orderDate">{ROSSOrderDateString}</span>
                            </Grid>
                        </Grid>
                        <Grid item xs={12}>
                            {
                                ROSSOrder.items.map((ROSSItem, i) => (
                                    <DesktopOrderListItem
                                        key={['_', i].join('')}
                                        classes={classes}
                                        orderNumber={ROSSOrder.orderNumber}
                                        index={i}
                                        uuid={ROSSOrder.uuid}
                                        userProfile={userProfile}
                                        ROSSOrder={ROSSOrder || null}
                                        ROSSItem={ROSSItem}
                                        addToCartError={addToCartError}
                                        showTrackingWidgetForSameLogicalItems={showTrackingWidgetForSameLogicalItems}
                                    />
                                ))
                            }
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
            {addToCartError?.length > 0 && scrollTop()}
        </div>
    );
};

DesktopOrderList.propTypes = {
    classes: object.isRequired,
    ROSSOrder: shape({
        brandId: string,
        modifiedDateTime: string,
        orderNumber: string,
        modifyOrder: string,
        uuid: string,
        status: string,
        items: arrayOf(
            shape({
                duration: string,
                interval: string,
                itemType: string,
                prdImgCtg: string,
                prdImgTmn: string,
                productName: string,
                productSku: string,
                seoUrl: string,
                subscriptionType: string,
            }),
        ),
    }),
    userProfile: shape({
        isAuthenticated: bool.isRequired,
    }).isRequired,
    showTrackingWidgetForSameLogicalItems: func,
};

DesktopOrderList.defaultProps = {
    ROSSOrder: {},
    showTrackingWidgetForSameLogicalItems: () => {},
};

export default (withStyles(styles)(DesktopOrderList));
